import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Clock, Users, ChefHat, ShoppingCart, Loader2, Settings, Plus, Database, Trash2, LogOut } from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import LoginForm from "@/components/LoginForm";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Global config state
let globalConfig = {
  backend_base_url: BACKEND_URL,
  environment: 'production'
};

// Helper function to construct full image URL from filename
const getImageUrl = (filename) => {
  if (!filename) return "https://images.unsplash.com/photo-1542838132-92c53300491e?w=400&h=400&fit=crop";
  
  // If already a full URL, return as is (for compatibility)
  if (filename.startsWith('http://') || filename.startsWith('https://')) {
    return filename;
  }
  
  // Construct URL using current backend base URL
  return `${globalConfig.backend_base_url}/api/images/${filename}`;
};

// Image regeneration state management - DISABLED
// const imageRegenerationState = {
//   regenerating: new Set(), // Track which images are being regenerated
//   failed: new Set() // Track which images have failed regeneration to prevent loops
// };

// Helper function to regenerate failed images - DISABLED
// const regenerateImage = async (imageType, recipeId, contextData, onSuccess, onError) => {
//   // Image regeneration is disabled for now
//   console.log(`🔄 Image regeneration is disabled, using fallback for ${imageType}`);
//   onError();
// };

const Header = ({ showAdminLink = false }) => {
  return (
    <header className="bg-white shadow-sm border-b sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center space-x-4">
            <ChefHat className="h-8 w-8 text-emerald-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900 font-serif">eRecipeGuide</h1>
              <p className="text-sm text-gray-600">AI-Powered Recipe Collection</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <Badge variant="secondary" className="bg-emerald-100 text-emerald-800">
              ✨ AI Generated
            </Badge>
            {showAdminLink && (
              <Button
                onClick={() => window.location.href = '/admin'}
                variant="outline"
                size="sm"
                className="flex items-center space-x-2"
              >
                <Settings className="h-4 w-4" />
                <span>Admin</span>
              </Button>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

const CategoryFilter = ({ categories, selectedCategory, onSelectCategory }) => {
  return (
    <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
      <h2 className="text-2xl font-bold text-gray-900 mb-6 font-serif">Browse by Category</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <Button
          variant={selectedCategory === "" ? "default" : "outline"}
          onClick={() => onSelectCategory("")}
          className={`h-12 text-sm font-medium transition-all duration-200 ${
            selectedCategory === "" 
              ? "bg-emerald-600 hover:bg-emerald-700 text-white" 
              : "hover:bg-emerald-50 hover:border-emerald-300"
          }`}
          data-testid="category-all"
        >
          All Recipes
        </Button>
        {categories.map((category) => (
          <Button
            key={category}
            variant={selectedCategory === category ? "default" : "outline"}
            onClick={() => onSelectCategory(category)}
            className={`h-12 text-sm font-medium capitalize transition-all duration-200 ${
              selectedCategory === category 
                ? "bg-emerald-600 hover:bg-emerald-700 text-white" 
                : "hover:bg-emerald-50 hover:border-emerald-300"
            }`}
            data-testid={`category-${category}`}
          >
            {category.replace('-', ' ')}
          </Button>
        ))}
      </div>
    </div>
  );
};

const RecipeCard = ({ recipe, onViewDetails }) => {
  return (
    <Card className="overflow-hidden hover:shadow-xl transition-all duration-300 bg-white border-0 shadow-lg">
      <div className="relative h-64 overflow-hidden">
        <img 
          src={getImageUrl(recipe.main_image_url)} 
          alt={recipe.title}
          className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
          loading="lazy"
          decoding="async"
          onError={(e) => {
            // Simple fallback without regeneration
            if (e.target.dataset.fallbackUsed) {
              return;
            }
            e.target.dataset.fallbackUsed = 'true';
            
            console.error(`🚨 Main image failed for recipe: ${recipe.title}`);
            console.error(`🔗 Failed URL: ${getImageUrl(recipe.main_image_url)}`);
            
            e.target.src = "https://images.unsplash.com/photo-1546548970-71785318a17b?w=800&h=600&fit=crop";
            console.warn(`🔄 Using fallback for main recipe image: ${recipe.title}`);
          }}
          onLoad={(e) => {
            console.log(`✅ SUCCESS: Loaded main recipe image: ${recipe.title}`);
          }}
        />
        <div className="absolute top-4 right-4">
          <Badge className="bg-white/90 text-gray-700 backdrop-blur-sm">
            {recipe.category}
          </Badge>
        </div>
        <div className="absolute top-4 left-4">
          <Badge variant="secondary" className={`${
            recipe.difficulty === 'Easy' ? 'bg-green-100 text-green-800' :
            recipe.difficulty === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
            'bg-red-100 text-red-800'
          }`}>
            {recipe.difficulty}
          </Badge>
        </div>
      </div>
      <CardHeader className="pb-3">
        <CardTitle className="text-xl font-bold text-gray-900 font-serif line-clamp-2">
          {recipe.title}
        </CardTitle>
        <CardDescription className="text-gray-600 line-clamp-2">
          {recipe.description}
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
          <div className="flex items-center space-x-4">
            <div className="flex items-center">
              <Clock className="h-4 w-4 mr-1" />
              {recipe.prep_time + recipe.cook_time} min
            </div>
            <div className="flex items-center">
              <Users className="h-4 w-4 mr-1" />
              {recipe.servings} servings
            </div>
          </div>
        </div>
        <Button 
          onClick={() => onViewDetails(recipe)}
          className="w-full bg-emerald-600 hover:bg-emerald-700 text-white transition-colors duration-200"
          data-testid={`view-recipe-${recipe.id}`}
        >
          View Recipe
        </Button>
      </CardContent>
    </Card>
  );
};

const RecipeDetails = ({ recipe, onBack }) => {
  const [showShoppingList, setShowShoppingList] = useState(false);

  return (
    <div className="max-w-4xl mx-auto">
      <Button 
        onClick={onBack}
        variant="outline"
        className="mb-6 hover:bg-gray-50"
        data-testid="back-to-recipes-btn"
      >
        ← Back to Recipes
      </Button>
      
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        {/* Hero Image */}
        <div className="relative h-96 overflow-hidden">
          <img 
            src={getImageUrl(recipe.main_image_url)} 
            alt={recipe.title}
            className="w-full h-full object-cover"
            loading="eager"
            decoding="async"
            onError={(e) => {
              console.error(`🚨 FALLBACK IMAGE USED FOR RECIPE DETAIL: ${recipe.title}`);
              console.error(`🔗 Failed URL: ${recipe.main_image_url}`);
              console.error(`🕒 Timestamp: ${new Date().toISOString()}`);
              
              // Test recipe detail image URL
              const testImg = new Image();
              testImg.onload = () => console.error(`✅ Recipe detail image accessible`);
              testImg.onerror = () => console.error(`❌ Recipe detail image not accessible`);
              testImg.src = recipe.main_image_url;
              
              e.target.src = "https://images.unsplash.com/photo-1546548970-71785318a17b?w=800&h=600&fit=crop";
              console.warn(`🔄 Using fallback for recipe detail image: ${recipe.title}`);
            }}
            onLoad={(e) => {
              console.log(`✅ SUCCESS: Loaded recipe detail image: ${recipe.title}`);
              console.log(`🔗 URL: ${recipe.main_image_url}`);
              console.log(`📊 Dimensions: ${e.target.naturalWidth}x${e.target.naturalHeight}`);
            }}
          />
          <div className="absolute inset-0 bg-black bg-opacity-20"></div>
          <div className="absolute bottom-6 left-6 text-white">
            <h1 className="text-4xl font-bold font-serif mb-2">{recipe.title}</h1>
            <p className="text-lg opacity-90">{recipe.description}</p>
          </div>
        </div>

        <div className="p-8">
          {/* Recipe Info */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
            <div className="text-center">
              <div className="text-2xl font-bold text-emerald-600">{recipe.prep_time}</div>
              <div className="text-sm text-gray-600">Prep Time (min)</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-emerald-600">{recipe.cook_time}</div>
              <div className="text-sm text-gray-600">Cook Time (min)</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-emerald-600">{recipe.servings}</div>
              <div className="text-sm text-gray-600">Servings</div>
            </div>
            <div className="text-center">
              <Badge className={`text-lg py-2 px-4 ${
                recipe.difficulty === 'Easy' ? 'bg-green-100 text-green-800' :
                recipe.difficulty === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                'bg-red-100 text-red-800'
              }`}>
                {recipe.difficulty}
              </Badge>
            </div>
          </div>

          <Separator className="my-8" />

          {/* Ingredients */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900 font-serif">Ingredients</h2>
              <Button
                onClick={() => setShowShoppingList(!showShoppingList)}
                variant="outline"
                className="flex items-center space-x-2"
                data-testid="toggle-shopping-list-btn"
              >
                <ShoppingCart className="h-4 w-4" />
                <span>{showShoppingList ? 'Hide' : 'Show'} Shopping List</span>
              </Button>
            </div>

            {showShoppingList && (
              <div className="bg-emerald-50 rounded-lg p-6 mb-6" data-testid="shopping-list">
                <h3 className="font-semibold text-emerald-800 mb-3">Shopping List</h3>
                <ul className="space-y-1">
                  {recipe.shopping_list?.map((item, index) => (
                    <li key={index} className="text-emerald-700">• {item}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {recipe.ingredients?.map((ingredient, index) => (
                <div key={index} className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
                  <img 
                    src={getImageUrl(ingredient.image_url)} 
                    alt={ingredient.name}
                    className="w-12 h-12 rounded-full object-cover"
                    loading="lazy"
                    decoding="async"
                    onError={(e) => {
                      // Simple fallback without regeneration
                      if (e.target.dataset.fallbackUsed) {
                        return;
                      }
                      e.target.dataset.fallbackUsed = 'true';
                      
                      console.error(`🚨 Ingredient image failed: ${ingredient.name}`);
                      e.target.src = `https://images.unsplash.com/photo-1542838132-92c53300491e?w=100&h=100&fit=crop&seed=${index}`;
                      console.warn(`🔄 Using fallback image for ${ingredient.name}`);
                    }}
                    onLoad={() => {
                      console.log(`✅ SUCCESS: Loaded ingredient image: ${ingredient.name}`);
                    }}
                  />
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">{ingredient.name}</div>
                    <div className="text-sm text-gray-600">{ingredient.amount} {ingredient.unit}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <Separator className="my-8" />

          {/* Instructions */}
          <div>
            <h2 className="text-2xl font-bold text-gray-900 font-serif mb-6">Instructions</h2>
            <div className="space-y-6">
              {recipe.instructions?.map((instruction, index) => (
                <div key={index} className="flex space-x-4" data-testid={`instruction-${index + 1}`}>
                  <div className="flex-shrink-0 w-8 h-8 bg-emerald-600 text-white rounded-full flex items-center justify-center font-semibold">
                    {instruction.step}
                  </div>
                  <div className="flex-1">
                    <p className="text-gray-700 leading-relaxed mb-3">{instruction.description}</p>
                    {instruction.technique_image_url && (
                      <img 
                        src={getImageUrl(instruction.technique_image_url)} 
                        alt={`Step ${instruction.step} technique`}
                        className="w-full max-w-md h-48 object-cover rounded-lg shadow-sm"
                        onError={(e) => {
                          // Simple fallback - hide the image if it fails
                          console.error(`🚨 Technique image failed: Step ${instruction.step}`);
                          e.target.style.display = 'none';
                          console.warn(`🙈 Hiding technique image for step ${instruction.step}`);
                        }}
                        onLoad={() => {
                          console.log(`✅ SUCCESS: Loaded technique image for step ${instruction.step}`);
                        }}
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const AdminDashboard = () => {
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [recipeCount, setRecipeCount] = useState(1);
  const [isGenerating, setIsGenerating] = useState(false);
  // const [isClearing, setIsClearing] = useState(false); // Disabled for production
  const [stats, setStats] = useState({ totalRecipes: 0, categoryCounts: {} });
  const [settings, setSettings] = useState({ backend_base_url: '', environment: '' });
  const [isUpdatingSettings, setIsUpdatingSettings] = useState(false);
  const [isDeletingContent, setIsDeletingContent] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const { logout, getToken } = useAuth();

  useEffect(() => {
    fetchConfig();
    fetchCategories();
    fetchStats();
    fetchSettings();
  }, []);

  const getAuthHeaders = () => {
    const token = getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API}/config`);
      globalConfig = response.data;
      console.log("📱 Updated global config:", globalConfig);
    } catch (error) {
      console.error("Error fetching config:", error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data.categories);
    } catch (error) {
      console.error("Error fetching categories:", error);
      toast.error("Failed to load categories");
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`, {
        headers: getAuthHeaders()
      });
      setStats(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("Session expired. Please log in again.");
        logout();
        return;
      }
      console.error("Error fetching stats:", error);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings`, {
        headers: getAuthHeaders()
      });
      setSettings(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("Session expired. Please log in again.");
        logout();
        return;
      }
      console.error("Error fetching settings:", error);
    }
  };

  const updateSettings = async () => {
    if (!settings.backend_base_url || !settings.environment) {
      toast.error("Please fill in all settings fields");
      return;
    }

    setIsUpdatingSettings(true);
    try {
      const response = await axios.post(`${API}/admin/settings`, {
        backend_base_url: settings.backend_base_url,
        environment: settings.environment
      }, {
        headers: getAuthHeaders()
      });
      
      toast.success("Settings updated successfully! 🎉");
      await fetchConfig(); // Refresh global config
      setShowSettings(false);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("Session expired. Please log in again.");
        logout();
        return;
      }
      console.error("Error updating settings:", error);
      toast.error("Failed to update settings. Please try again.");
    } finally {
      setIsUpdatingSettings(false);
    }
  };

  const migrateImageUrls = async () => {
    setIsUpdatingSettings(true);
    try {
      const response = await axios.post(`${API}/admin/migrate-image-urls`, {}, {
        headers: getAuthHeaders()
      });
      
      toast.success(`Migration complete! Updated ${response.data.updated_count} recipes 🎉`);
      await fetchConfig(); // Refresh config
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("Session expired. Please log in again.");
        logout();
        return;
      }
      console.error("Error migrating URLs:", error);
      toast.error("Failed to migrate URLs. Please try again.");
    } finally {
      setIsUpdatingSettings(false);
    }
  };

  const deleteAllContent = async () => {
    setIsDeletingContent(true);
    try {
      const response = await axios.delete(`${API}/admin/delete-all-content`, {
        headers: getAuthHeaders()
      });
      
      toast.success(`🗑️ All content deleted! Removed ${response.data.deleted_recipes} recipes and ${response.data.deleted_images} images.`);
      fetchStats(); // Refresh stats to show empty state
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("Session expired. Please log in again.");
        logout();
        return;
      }
      console.error("Error deleting content:", error);
      toast.error("Failed to delete content. Please try again.");
    } finally {
      setIsDeletingContent(false);
    }
  };

  const generateRecipes = async () => {
    if (!selectedCategory || recipeCount < 1) {
      toast.error("Please select a category and enter a valid count");
      return;
    }

    setIsGenerating(true);
    try {
      // Increase timeout for AI generation (5 minutes per recipe + buffer)
      const timeoutMs = recipeCount * 5 * 60 * 1000 + 60000; // 5 min per recipe + 1 min buffer
      
      const response = await axios.post(`${API}/admin/generate-batch`, {
        category: selectedCategory,
        count: recipeCount
      }, {
        timeout: timeoutMs,
        headers: getAuthHeaders()
      });
      
      toast.success(`Successfully generated ${recipeCount} ${selectedCategory} recipe${recipeCount > 1 ? 's' : ''}! 🍽️ 
      Created ${response.data.recipes?.length || recipeCount} recipes with full AI content and permanent image storage.`);
      fetchStats(); // Refresh stats
    } catch (error) {
      console.error("Error generating recipes:", error);
      
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast.error(`Generation timed out. This can happen with large batches. Try generating fewer recipes at once (1-2 recommended).`);
      } else if (error.response?.status === 500) {
        toast.error(`Server error during generation: ${error.response?.data?.detail || 'Unknown error'}. Please try again.`);
      } else {
        toast.error("Failed to generate recipes. Please try again with fewer recipes.");
      }
    } finally {
      setIsGenerating(false);
    }
  };

  // clearAllRecipes function disabled for production safety

  return (
    <div className="min-h-screen bg-gray-50">
      <Header showAdminLink={false} />
      <div className="container mx-auto px-4 py-8">
        {/* Admin Header */}
        <div className="text-center mb-12">
          <div className="flex justify-between items-center mb-8">
            <div></div>
            <div>
              <h1 className="text-5xl font-bold text-gray-900 font-serif mb-4">
                Admin Dashboard
              </h1>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Manage your AI recipe collection. Generate recipes in batches by category.
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <Button
                onClick={() => setShowSettings(!showSettings)}
                variant="outline"
                className="flex items-center space-x-2"
              >
                <Settings className="h-4 w-4" />
                <span>Settings</span>
              </Button>
              <Button
                onClick={logout}
                variant="outline"
                className="flex items-center space-x-2"
                data-testid="logout-btn"
              >
                <LogOut className="h-4 w-4" />
                <span>Logout</span>
              </Button>
            </div>
          </div>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Recipes</CardTitle>
              <Database className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalRecipes}</div>
              <p className="text-xs text-muted-foreground">
                Recipes in database
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Categories</CardTitle>
              <ChefHat className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{categories.length}</div>
              <p className="text-xs text-muted-foreground">
                Available categories
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Most Popular</CardTitle>
              <Plus className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {Object.keys(stats.categoryCounts).length > 0 
                  ? Object.entries(stats.categoryCounts).sort(([,a], [,b]) => b - a)[0][0]
                  : "N/A"
                }
              </div>
              <p className="text-xs text-muted-foreground">
                Category with most recipes
              </p>
            </CardContent>
          </Card>
        </div>

        {/* App Settings */}
        {showSettings && (
          <Card className="max-w-2xl mx-auto mb-8 border-blue-200">
            <CardHeader>
              <CardTitle className="text-2xl font-bold text-blue-700 font-serif">
                App Settings
              </CardTitle>
              <CardDescription>
                Configure deployment settings and environment variables.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Backend Base URL</label>
                <Input
                  type="url"
                  value={settings.backend_base_url}
                  onChange={(e) => setSettings({...settings, backend_base_url: e.target.value})}
                  placeholder="https://erecipeguide.com"
                  className="w-full"
                />
                <p className="text-xs text-gray-500">
                  The base URL for your deployed site (affects image URLs)
                </p>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Environment</label>
                <Select 
                  value={settings.environment} 
                  onValueChange={(value) => setSettings({...settings, environment: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select environment" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="development">Development (Preview Site)</SelectItem>
                    <SelectItem value="production">Production (Live Site)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-medium text-blue-800 mb-2">Quick Settings:</h4>
                <div className="space-y-2">
                  <Button
                    onClick={() => setSettings({
                      backend_base_url: 'https://erecipeguide.com',
                      environment: 'production'
                    })}
                    variant="outline"
                    size="sm"
                    className="w-full"
                  >
                    🌐 Set to erecipeguide.com (Production)
                  </Button>
                  <Button
                    onClick={() => setSettings({
                      backend_base_url: 'https://speedy-recipes.preview.emergentagent.com',
                      environment: 'development'
                    })}
                    variant="outline"
                    size="sm"
                    className="w-full"
                  >
                    🔧 Set to Preview Site (Development)
                  </Button>
                </div>
              </div>

              <Button 
                onClick={updateSettings}
                disabled={isUpdatingSettings}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              >
                {isUpdatingSettings ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Updating Settings...
                  </>
                ) : (
                  'Update Settings'
                )}
              </Button>

              <div className="pt-4 border-t">
                <p className="text-sm text-gray-600 mb-2">
                  🔄 <strong>Migration:</strong> Convert old full URLs to filename-only format
                </p>
                <Button 
                  onClick={migrateImageUrls}
                  disabled={isUpdatingSettings}
                  variant="outline"
                  className="w-full"
                >
                  Migrate Existing Image URLs
                </Button>
              </div>

              <div className="text-center">
                <Button
                  onClick={() => setShowSettings(false)}
                  variant="ghost"
                  size="sm"
                >
                  Close Settings
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recipe Generation */}
        <Card className="max-w-2xl mx-auto">
          <CardHeader>
            <CardTitle className="text-2xl font-bold text-gray-900 font-serif">
              Generate Recipes
            </CardTitle>
            <CardDescription>
              Select a category and specify how many recipes to generate using AI.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Category</label>
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a recipe category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((category) => (
                    <SelectItem key={category} value={category}>
                      {category.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      {stats.categoryCounts[category] ? ` (${stats.categoryCounts[category]} existing)` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Number of Recipes</label>
              <Input
                type="number"
                min="1"
                max="10"
                value={recipeCount}
                onChange={(e) => setRecipeCount(parseInt(e.target.value) || 1)}
                placeholder="Enter number of recipes (1-10)"
              />
              <p className="text-xs text-gray-500">
                Each recipe includes AI-generated content, main image, ingredient images, and technique photos.
              </p>
            </div>

            <Button 
              onClick={generateRecipes}
              disabled={!selectedCategory || isGenerating || recipeCount < 1}
              size="lg"
              className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Generating {recipeCount} Recipe{recipeCount > 1 ? 's' : ''}...
                </>
              ) : (
                <>
                  <Plus className="mr-2 h-4 w-4" />
                  Generate {recipeCount} Recipe{recipeCount > 1 ? 's' : ''}
                </>
              )}
            </Button>

            {isGenerating && (
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-blue-700">
                  🤖 AI is generating your recipes... This may take 3-5 minutes per recipe as we create:
                </p>
                <ul className="text-xs text-blue-600 mt-2 space-y-1">
                  <li>• Recipe content with GPT-5</li>
                  <li>• Professional food photography with DALL-E 3</li>
                  <li>• Individual ingredient images (6-12 per recipe)</li>
                  <li>• Cooking technique demonstrations</li>
                  <li>• Image processing and permanent storage</li>
                </ul>
                <div className="mt-3 flex items-center space-x-2">
                  <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                  <span className="text-sm text-blue-600">
                    Please wait... Generating {recipeCount} recipe{recipeCount > 1 ? 's' : ''} with full AI content
                  </span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Category Breakdown */}
        {Object.keys(stats.categoryCounts).length > 0 && (
          <Card className="mt-12">
            <CardHeader>
              <CardTitle className="text-xl font-bold text-gray-900 font-serif">
                Recipe Distribution
              </CardTitle>
              <CardDescription>
                Number of recipes per category
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
                {Object.entries(stats.categoryCounts).map(([category, count]) => (
                  <div key={category} className="text-center p-4 bg-gray-50 rounded-lg">
                    <div className="text-2xl font-bold text-emerald-600">{count}</div>
                    <div className="text-sm text-gray-600 capitalize">
                      {category.replace('-', ' ')}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Database Management */}
        {(() => {
          console.log("🔍 Rendering Database Management section...");
          console.log("stats:", stats);
          return true;
        })() && (
          <Card className="mt-12 border-red-200">
            <CardHeader>
              <CardTitle className="text-xl font-bold text-red-700 font-serif">
                Database Management
              </CardTitle>
              <CardDescription className="text-gray-600">
                ⚠️ Danger Zone - Permanent content deletion operations
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="bg-red-50 border border-red-200 p-6 rounded-lg">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <Trash2 className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-red-800 mb-2">Delete All Content</h3>
                    <p className="text-sm text-red-700 mb-4">
                      This will permanently delete all {stats.totalRecipes || 0} recipes and all associated images from the server. 
                      This action cannot be undone.
                    </p>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button 
                          variant="destructive" 
                          disabled={isDeletingContent}
                          className="bg-red-600 hover:bg-red-700"
                        >
                          {isDeletingContent ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Deleting All Content...
                            </>
                          ) : (
                            <>
                              <Trash2 className="mr-2 h-4 w-4" />
                              Delete All Content
                            </>
                          )}
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle className="text-red-700">⚠️ Confirm Content Deletion</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will permanently delete:
                            <br />• All {stats.totalRecipes || 0} recipes from the database
                            <br />• All generated images from the server
                            <br />• This action cannot be undone
                            <br /><br />
                            Are you absolutely sure you want to proceed?
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction 
                            onClick={deleteAllContent}
                            className="bg-red-600 hover:bg-red-700"
                          >
                            Yes, Delete Everything
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Back to Website */}
        <div className="text-center mt-12">
          <Button
            onClick={() => window.location.href = '/'}
            variant="outline"
            size="lg"
          >
            ← Back to Website
          </Button>
        </div>
      </div>
    </div>
  );
};

const Home = () => {
  const [recipes, setRecipes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadConfig = async () => {
      try {
        const response = await axios.get(`${API}/config`);
        globalConfig = response.data;
        console.log("📱 Loaded global config:", globalConfig);
      } catch (error) {
        console.error("Error loading config:", error);
      }
    };
    
    loadConfig();
    fetchCategories();
    fetchRecipes();
  }, []);

  useEffect(() => {
    fetchRecipes(selectedCategory);
  }, [selectedCategory]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data.categories);
    } catch (error) {
      console.error("Error fetching categories:", error);
      toast.error("Failed to load categories");
    }
  };

  const fetchRecipes = async (category = null) => {
    try {
      setLoading(true);
      const params = category ? `?category=${category}` : '';
      const response = await axios.get(`${API}/recipes${params}`);
      setRecipes(response.data);
    } catch (error) {
      console.error("Error fetching recipes:", error);
      toast.error("Failed to load recipes");
    } finally {
      setLoading(false);
    }
  };

  const viewRecipeDetails = (recipe) => {
    setSelectedRecipe(recipe);
  };

  const backToRecipes = () => {
    setSelectedRecipe(null);
  };

  // Image regeneration functionality removed for stability

  if (selectedRecipe) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header showAdminLink={true} />
        <div className="container mx-auto px-4 py-8">
          <RecipeDetails 
            recipe={selectedRecipe} 
            onBack={backToRecipes} 
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header showAdminLink={true} />
      <div className="container mx-auto px-4 py-8">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold text-gray-900 font-serif mb-4">
            Discover Amazing Recipes
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Explore our collection of AI-generated recipes with beautiful images and detailed instructions. 
            Each recipe is unique and crafted with professional techniques.
          </p>
        </div>

        {/* Category Filter */}
        <CategoryFilter
          categories={categories}
          selectedCategory={selectedCategory}
          onSelectCategory={setSelectedCategory}
        />

        {/* Recipe Grid */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-3xl font-bold text-gray-900 font-serif">
              {selectedCategory ? `${selectedCategory.replace('-', ' ')} Recipes`.replace(/\b\w/g, l => l.toUpperCase()) : 'All Recipes'}
            </h2>
            <Button
              onClick={() => fetchRecipes(selectedCategory)}
              variant="outline"
              data-testid="refresh-recipes-btn"
            >
              Refresh
            </Button>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <Card key={i} className="overflow-hidden">
                  <div className="h-64 bg-gray-200 animate-pulse"></div>
                  <CardHeader>
                    <div className="h-6 bg-gray-200 animate-pulse rounded mb-2"></div>
                    <div className="h-4 bg-gray-200 animate-pulse rounded"></div>
                  </CardHeader>
                </Card>
              ))}
            </div>
          ) : recipes.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="recipes-grid">
              {recipes.map((recipe) => (
                <RecipeCard
                  key={recipe.id}
                  recipe={recipe}
                  onViewDetails={viewRecipeDetails}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <ChefHat className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-600 mb-2">
                No recipes found {selectedCategory && `in ${selectedCategory.replace('-', ' ')}`}
              </h3>
              <p className="text-gray-500">
                {selectedCategory 
                  ? "Try selecting a different category or check back later for new recipes!"
                  : "Our chefs are working on creating amazing recipes for you!"
                }
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ProtectedAdminRoute = () => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-emerald-600" />
          <p className="text-gray-600">Checking authentication...</p>
        </div>
      </div>
    );
  }

  return isAuthenticated ? <AdminDashboard /> : <LoginForm />;
};

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/admin" element={<ProtectedAdminRoute />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" />
      </AuthProvider>
    </div>
  );
}

export default App;